import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPrice, getImageUrl } from '../utils/currency';
import { Building, CheckCircle, XCircle, X, Package, Trash2, Eye } from 'lucide-react';
import countries, { getCountryByCode } from '../data/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCompanies({ companies, searchCompanies, setSearchCompanies, statusFilter, fetchData, getStatusBadge }) {
  const { t, language } = useLanguage();
  const [editingCompany, setEditingCompany] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', phone: '', address: '', country: '', account_type: '', picture: '' });

  const filteredCompanies = companies.filter(company => {
    const matchSearch = !searchCompanies || company.name?.toLowerCase().includes(searchCompanies.toLowerCase()) ||
      company.email?.toLowerCase().includes(searchCompanies.toLowerCase()) || company.phone?.includes(searchCompanies);
    const matchStatus = statusFilter === 'all' || company.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleCompanyStatus = async (companyId, status) => {
    try {
      await axios.patch(`${API}/admin/companies/${companyId}/status?status=${status}`);
      toast.success(status === 'approved' ? t('companyApproved') : t('companyRejected'));
      fetchData();
    } catch (error) { console.error(error); }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setEditForm({
      name: company.name || '', description: company.description || '', phone: company.phone || '',
      address: company.address || '', country: company.country || '', account_type: company.account_type || 'both', picture: company.picture || ''
    });
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/companies/${editingCompany.id}`, editForm);
      toast.success(t('companyUpdated'));
      setEditingCompany(null);
      fetchData();
    } catch (error) { console.error(error); }
  };

  const handleSuspendActivate = async (companyId, currentStatus) => {
    const newStatus = currentStatus === 'suspended' ? 'approved' : 'suspended';
    try {
      await axios.patch(`${API}/admin/companies/${companyId}/status?status=${newStatus}`);
      toast.success(newStatus === 'approved' ? t('companyActivated') : t('companySuspended'));
      fetchData();
    } catch (error) { console.error(error); }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(language === 'ar' ? `هل تريد حذف الشركة "${companyName}" وجميع بياناتها؟` : `Delete company "${companyName}" and all its data?`)) return;
    try {
      await axios.delete(`${API}/admin/companies/${companyId}`);
      toast.success(language === 'ar' ? 'تم حذف الشركة بنجاح' : 'Company deleted successfully');
      fetchData();
    } catch (error) { console.error(error); }
  };

  return (
    <>
      <div className="mb-6">
        <input type="text" placeholder={t('searchCompanies')} value={searchCompanies} onChange={(e) => setSearchCompanies(e.target.value)}
          data-testid="search-companies-input" className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent" />
      </div>
      <div className="space-y-4">
        {filteredCompanies.map((company) => (
          <div key={company.id} className="bg-white p-4 sm:p-6 border border-border rounded-xl" data-testid={`company-card-${company.id}`}>
            <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {company.picture ? (
                  <img src={getImageUrl(company.picture)} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-border flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Building size={20} className="text-accent/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-xl font-heading font-bold text-primary mb-1 sm:mb-2 truncate">{company.name}</h3>
                  <p className="text-muted-foreground text-xs sm:text-sm mb-1 sm:mb-2 line-clamp-2">{company.description}</p>
                  {company.email && <p className="text-xs sm:text-sm text-accent font-medium mb-1 truncate">📧 {company.email}</p>}
                  {company.phone && <p className="text-xs sm:text-sm text-muted-foreground">{language === 'ar' ? 'الهاتف' : 'Phone'}: {company.phone}</p>}
                  {company.address && <p className="text-xs sm:text-sm text-muted-foreground truncate">{language === 'ar' ? 'العنوان' : 'Address'}: {company.address}</p>}
                  {company.country && (() => { const c = getCountryByCode(company.country); return c ? <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1.5"><span className="text-base">{c.flag}</span> {language === 'ar' ? c.name_ar : c.name_en}</p> : null; })()}
                  {company.account_type && (
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${company.account_type === 'seller' ? 'bg-blue-100 text-blue-700' : company.account_type === 'buyer' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`} data-testid={`company-type-${company.id}`}>
                      {company.account_type === 'seller' ? (language === 'ar' ? 'بائع' : 'Seller') : company.account_type === 'buyer' ? (language === 'ar' ? 'مشتري' : 'Buyer') : (language === 'ar' ? 'بائع ومشتري' : 'Both')}
                    </span>
                  )}
                </div>
              </div>
              {getStatusBadge(company.status)}
            </div>
            {company.status === 'pending' && (
              <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-border">
                <button onClick={() => handleCompanyStatus(company.id, 'approved')} data-testid={`approve-company-${company.id}`} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs sm:text-sm font-semibold transition-all"><CheckCircle size={16} />{t('accept')}</button>
                <button onClick={() => handleCompanyStatus(company.id, 'rejected')} data-testid={`reject-company-${company.id}`} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs sm:text-sm font-semibold transition-all"><XCircle size={16} />{t('reject')}</button>
                <button onClick={() => handleDeleteCompany(company.id, company.name)} data-testid={`delete-pending-company-${company.id}`} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 text-xs sm:text-sm font-semibold transition-all"><Trash2 size={16} />{language === 'ar' ? 'حذف' : 'Delete'}</button>
              </div>
            )}
            {(company.status === 'approved' || company.status === 'suspended') && (
              <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-border">
                <button onClick={() => handleEditCompany(company)} data-testid={`edit-company-${company.id}`} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs sm:text-sm font-semibold transition-all"><Package size={18} />{t('edit')}</button>
                <button onClick={() => handleSuspendActivate(company.id, company.status)} data-testid={`suspend-activate-company-${company.id}`} className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-semibold transition-all ${company.status === 'suspended' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}>{company.status === 'suspended' ? <CheckCircle size={18} /> : <XCircle size={18} />}{company.status === 'suspended' ? t('activate') : t('suspend')}</button>
                <button onClick={() => handleDeleteCompany(company.id, company.name)} data-testid={`delete-company-${company.id}`} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 text-xs sm:text-sm font-semibold transition-all"><Trash2 size={16} />{language === 'ar' ? 'حذف' : 'Delete'}</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Company Modal */}
      {editingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingCompany(null)}>
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">{t('editCompany')}</h2>
            <form onSubmit={handleUpdateCompany} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('companyName')}</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} required className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('companyDescription')}</label>
                <textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} required rows={3} className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent resize-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t('phone')}</label>
                  <input type="tel" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t('address')}</label>
                  <input type="text" value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('country')}</label>
                <select value={editForm.country} onChange={(e) => setEditForm({...editForm, country: e.target.value})} data-testid="edit-company-country" className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white">
                  <option value="">{t('selectCountry')}</option>
                  {countries.map(c => <option key={c.code} value={c.code}>{c.flag} {language === 'ar' ? c.name_ar : c.name_en}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{language === 'ar' ? 'نوع الحساب' : 'Account Type'}</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{value:'seller',labelAr:'بائع',labelEn:'Seller'},{value:'buyer',labelAr:'مشتري',labelEn:'Buyer'},{value:'both',labelAr:'بائع ومشتري',labelEn:'Both'}].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setEditForm({...editForm, account_type: opt.value})} data-testid={`edit-account-type-${opt.value}`}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${editForm.account_type === opt.value ? 'bg-accent text-white border-accent' : 'bg-white text-foreground border-border hover:border-accent/50'}`}>
                      {language === 'ar' ? opt.labelAr : opt.labelEn}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{language === 'ar' ? 'صورة البروفايل' : 'Profile Picture'}</label>
                <div className="flex items-center gap-4">
                  {editForm.picture ? (
                    <div className="relative">
                      <img src={getImageUrl(editForm.picture)} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-border" />
                      <button type="button" onClick={() => setEditForm({...editForm, picture: ''})} className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full"><X size={12} /></button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center"><Building size={24} className="text-gray-400" /></div>
                  )}
                  <input type="file" accept="image/*" data-testid="edit-company-picture-input" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    const fd = new FormData(); fd.append('file', file);
                    try { const { data } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setEditForm({...editForm, picture: data.url}); }
                    catch { toast.error(language === 'ar' ? 'فشل رفع الصورة' : 'Upload failed'); }
                  }} className="text-sm" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 px-4 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold transition-all">{t('updateCompany')}</button>
                <button type="button" onClick={() => setEditingCompany(null)} className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold transition-all">{t('cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
